import { useCallback, useEffect, useRef, useState } from 'react';
import { Upload, X } from 'lucide-react';
import { Loader } from './ui/Loader';
import { motion, AnimatePresence } from 'motion/react';
import { FullscreenShell } from './layout/FullscreenShell';
import { useModalFocus } from '../utils/hooks/useModalFocus';
import { API_BASE } from '../utils/supabase/info';
import { toast } from '../utils/toast';
import { canCreateTalentPackage } from '../utils/accessPolicy';
import { validateText } from '../utils/profanity-filter';
import { devError } from '../utils/logger';
import { compressImageForUpload, PORTFOLIO_PRESET } from '../utils/imageCompression';
import { useDirtyFormGuard } from '../utils/hooks/useDirtyFormGuard';
import { useFormDraftPersistence } from '../utils/hooks/useFormDraftPersistence';
import { useBeforeUnloadDirty } from '../utils/hooks/useBeforeUnloadDirty';
import { useImageCropModal } from '../utils/hooks/useImageCropModal';
import { CONTENT_IMAGE_SPEC_4_3 } from '../utils/media/imageCrop';
import { ConfirmDialog } from './ConfirmDialog';

const MAX_IMAGES = 3;
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

function getInitialCoverUrl(pkg: any): string | null {
  const cover = pkg?.coverImageUrl ?? pkg?.cover_image_url;
  if (cover && typeof cover === 'string' && cover.trim()) return cover.trim();
  const arr = pkg?.imageUrls ?? pkg?.image_urls;
  if (Array.isArray(arr) && arr.length > 0 && typeof arr[0] === 'string') return arr[0].trim();
  const single = pkg?.imageUrl ?? pkg?.image_url;
  if (single && typeof single === 'string' && single.trim()) return single.trim();
  return null;
}

function getInitialImageUrls(pkg: any): string[] {
  const arr = pkg?.imageUrls ?? pkg?.image_urls;
  if (!Array.isArray(arr)) return [];
  return arr.filter((u: unknown) => typeof u === 'string' && (u as string).trim()).map((u: string) => (u as string).trim()).slice(0, MAX_IMAGES);
}

interface CreateTalentPackageModalProps {
  accessToken: string;
  userProfile: any;
  onClose: () => void;
  onSuccess: () => void;
  onNavigateToSubscription?: () => void;
  initialPackage?: any | null;
}

export function CreateTalentPackageModal({
  accessToken,
  userProfile,
  onClose,
  onSuccess,
  onNavigateToSubscription,
  initialPackage,
}: CreateTalentPackageModalProps) {
  useModalFocus(true, { fullscreen: true });
  const isEditMode = !!initialPackage?.id;
  const hasAccess = canCreateTalentPackage(userProfile);
  const isIosPwa = typeof document !== 'undefined' && document.documentElement.classList.contains('ios-pwa');
  const isAndroidPwa = typeof document !== 'undefined' && document.documentElement.classList.contains('android-pwa');
  const [title, setTitle] = useState(initialPackage?.title || '');
  const [description, setDescription] = useState(initialPackage?.description || '');
  const [priceMin, setPriceMin] = useState(
    initialPackage?.priceMin ?? initialPackage?.price_min != null ? String(initialPackage?.priceMin ?? initialPackage?.price_min) : ''
  );
  const [priceMax, setPriceMax] = useState(
    initialPackage?.priceMax ?? initialPackage?.price_max != null ? String(initialPackage?.priceMax ?? initialPackage?.price_max) : ''
  );
  const [county, setCounty] = useState(initialPackage?.county || '');
  const [coverImageUrl, setCoverImageUrl] = useState<string | null>(() => getInitialCoverUrl(initialPackage));
  const [coverImagePreview, setCoverImagePreview] = useState<string | null>(null);
  const [existingImageUrls, setExistingImageUrls] = useState<string[]>(() => getInitialImageUrls(initialPackage));
  const [newImagePreviews, setNewImagePreviews] = useState<string[]>([]);
  const [compressing, setCompressing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const userHasEditedSinceApply = useRef(false);
  const { isDirty, markDirty, resetDirty, setDirty } = useDirtyFormGuard(false);
  const { openCrop, cropModal } = useImageCropModal();

  const handleRestoreDraft = useCallback((payload: {
    title: string;
    description: string;
    priceMin: string;
    priceMax: string;
    county: string;
    coverImageUrl: string | null;
    coverImagePreview: string | null;
    existingImageUrls: string[];
    newImagePreviews: string[];
  }) => {
    setTitle(payload?.title || '');
    setDescription(payload?.description || '');
    setPriceMin(payload?.priceMin || '');
    setPriceMax(payload?.priceMax || '');
    setCounty(payload?.county || '');
    setCoverImageUrl(payload?.coverImageUrl ?? null);
    setCoverImagePreview(payload?.coverImagePreview ?? null);
    setExistingImageUrls(Array.isArray(payload?.existingImageUrls) ? payload.existingImageUrls.slice(0, MAX_IMAGES) : []);
    setNewImagePreviews(Array.isArray(payload?.newImagePreviews) ? payload.newImagePreviews.slice(0, MAX_IMAGES) : []);
    userHasEditedSinceApply.current = true;
    setDirty(true);
  }, [setDirty]);

  const { clearDraft } = useFormDraftPersistence({
    key: `talentfunn:draft:package-modal:${userProfile?.id ?? 'me'}:${initialPackage?.id ?? 'new'}`,
    enabled: true,
    value: { title, description, priceMin, priceMax, county, coverImageUrl, coverImagePreview, existingImageUrls, newImagePreviews },
    onRestore: handleRestoreDraft,
  });

  useBeforeUnloadDirty(isDirty && !submitting);

  useEffect(() => {
    setTitle(initialPackage?.title || '');
    setDescription(initialPackage?.description || '');
    setPriceMin(initialPackage?.priceMin ?? initialPackage?.price_min != null ? String(initialPackage?.priceMin ?? initialPackage?.price_min) : '');
    setPriceMax(initialPackage?.priceMax ?? initialPackage?.price_max != null ? String(initialPackage?.priceMax ?? initialPackage?.price_max) : '');
    setCounty(initialPackage?.county || '');
    setCoverImageUrl(getInitialCoverUrl(initialPackage));
    setCoverImagePreview(null);
    setExistingImageUrls(getInitialImageUrls(initialPackage));
    setNewImagePreviews([]);
    resetDirty();
    userHasEditedSinceApply.current = false;
  }, [initialPackage, resetDirty]);

  const closeWithCleanup = useCallback(() => {
    clearDraft();
    resetDirty();
    onClose();
  }, [clearDraft, resetDirty, onClose]);

  const requestClose = useCallback(() => {
    if (isDirty) {
      setShowDiscardConfirm(true);
      return;
    }
    closeWithCleanup();
  }, [isDirty, closeWithCleanup]);

  const confirmDiscard = useCallback(() => {
    setShowDiscardConfirm(false);
    closeWithCleanup();
  }, [closeWithCleanup]);

  const displayCover = coverImagePreview ?? coverImageUrl;
  const totalImageCount = existingImageUrls.length + newImagePreviews.length;
  const remainingSlots = Math.max(0, MAX_IMAGES - totalImageCount);

  const handleCoverImageChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_FILE_SIZE) {
      toast.error(`Bildet "${file.name}" er for stort. Maks 10MB.`);
      return;
    }
    if (!file.type.startsWith('image/')) {
      toast.error(`"${file.name}" er ikke et gyldig bilde.`);
      return;
    }
    e.target.value = '';
    setCompressing(true);
    try {
      const croppedFile = await openCrop(file, CONTENT_IMAGE_SPEC_4_3);
      const compressed = await compressImageForUpload(croppedFile, PORTFOLIO_PRESET);
      setCoverImagePreview(compressed);
      setCoverImageUrl(null);
      userHasEditedSinceApply.current = true;
      markDirty();
    } catch (err) {
      if (err instanceof Error && err.message.includes('avbrutt')) return;
      devError('CreateTalentPackageModal cover compress', err);
      toast.error('Kunne ikke behandle bildet.');
    } finally {
      setCompressing(false);
    }
  }, [openCrop, markDirty]);

  const removeCoverImage = useCallback(() => {
    userHasEditedSinceApply.current = true;
    markDirty();
    setCoverImagePreview(null);
    setCoverImageUrl(null);
  }, [markDirty]);

  const handleImageChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files?.length || remainingSlots <= 0) return;
    const toAdd = Array.from(files).slice(0, remainingSlots);
    for (const file of toAdd) {
      if (file.size > MAX_FILE_SIZE) {
        toast.error(`Bildet "${file.name}" er for stort. Maks 10MB.`);
        return;
      }
      if (!file.type.startsWith('image/')) {
        toast.error(`"${file.name}" er ikke et gyldig bilde.`);
        return;
      }
    }
    e.target.value = '';
    setCompressing(true);
    try {
      const compressedPreviews = await Promise.all(
        toAdd.map((file) => compressImageForUpload(file, PORTFOLIO_PRESET))
      );
      setNewImagePreviews((prev) => [...prev, ...compressedPreviews].slice(0, MAX_IMAGES - existingImageUrls.length));
      userHasEditedSinceApply.current = true;
      markDirty();
    } catch (err) {
      devError('CreateTalentPackageModal image compress', err);
      toast.error('Kunne ikke behandle bildene.');
    } finally {
      setCompressing(false);
    }
  }, [remainingSlots, existingImageUrls.length, markDirty]);

  const removeExistingImage = useCallback((index: number) => {
    userHasEditedSinceApply.current = true;
    markDirty();
    setExistingImageUrls((prev) => prev.filter((_, i) => i !== index));
  }, [markDirty]);

  const removeNewImage = useCallback((index: number) => {
    userHasEditedSinceApply.current = true;
    markDirty();
    setNewImagePreviews((prev) => prev.filter((_, i) => i !== index));
  }, [markDirty]);

  const handleSubmit = async () => {
    if (!hasAccess) {
      toast.error('Talentfunn Pluss kreves for å administrere tjenester');
      closeWithCleanup();
      onNavigateToSubscription?.();
      return;
    }

    const trimmedTitle = title.trim();
    const trimmedDescription = description.trim();
    const parsedPriceMin = priceMin.trim() ? Number(priceMin.trim()) : null;
    const parsedPriceMax = priceMax.trim() ? Number(priceMax.trim()) : null;

    if (trimmedTitle.length < 3 || trimmedTitle.length > 100) {
      toast.error('Tittel må være mellom 3 og 100 tegn');
      return;
    }
    if (trimmedDescription.length < 10 || trimmedDescription.length > 2000) {
      toast.error('Beskrivelse må være mellom 10 og 2000 tegn');
      return;
    }
    const titleValidation = validateText(trimmedTitle, 'Tittel');
    if (!titleValidation.isValid) {
      toast.error(titleValidation.error || 'Tittel inneholder upassende språk. Vennligst bruk et profesjonelt språk.');
      return;
    }
    const descriptionValidation = validateText(trimmedDescription, 'Beskrivelse');
    if (!descriptionValidation.isValid) {
      toast.error(descriptionValidation.error || 'Beskrivelse inneholder upassende språk. Vennligst bruk et profesjonelt språk.');
      return;
    }
    const trimmedCounty = county.trim();
    if (trimmedCounty) {
      const countyValidation = validateText(trimmedCounty, 'Sted');
      if (!countyValidation.isValid) {
        toast.error(countyValidation.error || 'Sted inneholder upassende språk. Vennligst bruk et profesjonelt språk.');
        return;
      }
    }
    if (parsedPriceMin !== null && (!Number.isFinite(parsedPriceMin) || parsedPriceMin < 0)) {
      toast.error('Minimumspris er ugyldig');
      return;
    }
    if (parsedPriceMax !== null && (!Number.isFinite(parsedPriceMax) || parsedPriceMax < 0)) {
      toast.error('Makspris er ugyldig');
      return;
    }
    if (parsedPriceMin !== null && parsedPriceMax !== null && parsedPriceMax < parsedPriceMin) {
      toast.error('Makspris må være lik eller høyere enn minimumspris');
      return;
    }

    setSubmitting(true);
    try {
      const endpoint = isEditMode ? `${API_BASE}/packages/${initialPackage.id}` : `${API_BASE}/packages`;
      const method = isEditMode ? 'PUT' : 'POST';
      const coverImageBase64 = coverImagePreview || undefined;
      const imageBase64 = newImagePreviews.length > 0 ? newImagePreviews.join(',') : undefined;
      const imageUrls = existingImageUrls;

      const payload: Record<string, unknown> = {
        title: trimmedTitle,
        description: trimmedDescription,
        county: county.trim() || null,
        priceMin: parsedPriceMin,
        priceMax: parsedPriceMax,
        status: initialPackage?.status || 'active',
      };
      if (coverImageBase64) payload.coverImageBase64 = coverImageBase64;
      if (isEditMode && !displayCover) payload.clearCoverImage = true;
      if (imageBase64) payload.imageBase64 = imageBase64;
      if (isEditMode) payload.imageUrls = imageUrls;

      const response = await fetch(endpoint, {
        method,
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        if (data?.requiresUpgrade) {
          toast.error(data?.error || 'Oppgradering kreves');
          closeWithCleanup();
          onNavigateToSubscription?.();
          return;
        }
        throw new Error(data?.error || 'Kunne ikke lagre tjeneste');
      }

      toast.success(isEditMode ? 'Tjeneste oppdatert' : 'Tjeneste opprettet');
      clearDraft();
      resetDirty();
      onSuccess();
      onClose();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunne ikke lagre tjeneste');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <ConfirmDialog
        isOpen={showDiscardConfirm}
        onClose={() => setShowDiscardConfirm(false)}
        onConfirm={confirmDiscard}
        title="Forkaste endringer?"
        message="Du har ulagrede endringer i dette skjemaet. Vil du forkaste disse?"
        confirmText="Forkast"
        cancelText="Fortsett redigering"
        variant="warning"
      />
      {cropModal}

      <AnimatePresence>
        {submitting && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 talentfunn-modal-overlay backdrop-blur-md flex items-center justify-center z-[100]"
          >
            <div className="bg-white rounded-3xl p-8 shadow-2xl max-w-md w-full mx-4">
              <div className="flex flex-col items-center gap-6">
                <div className="relative" aria-hidden="true">
                  <Loader size={60} className="text-purple-600" aria-hidden="true" />
                </div>
                <div className="text-center">
                  <h3 className="text-xl text-gray-900 mb-2">
                    {isEditMode ? 'Lagrer...' : 'Publiserer tjeneste...'}
                  </h3>
                  <p className="text-gray-600 text-sm">Dette kan ta noen sekunder</p>
                </div>
                <div className="w-full bg-purple-100 rounded-full h-2 overflow-hidden" aria-hidden="true">
                  <motion.div
                    className="h-full bg-gradient-to-r from-purple-600 to-pink-600"
                    initial={{ width: '0%' }}
                    animate={{ width: '100%' }}
                    transition={{ duration: 3, ease: 'easeInOut' }}
                  />
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    <FullscreenShell
      title={isEditMode ? 'Rediger tjeneste' : 'Opprett tjeneste'}
      onClose={requestClose}
      onBack={requestClose}
      containerClassName="xl:max-w-2xl"
      contentClassName="edit-profile-drawer-content space-y-4 pb-10 xl:pb-16"
      overlayClassName="gig-modal-overlay gig-modal-overlay-create"
      compactSpacer={isIosPwa || isAndroidPwa}
    >
      {!hasAccess ? (
        <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-6 rounded-xl border border-purple-200">
          <h3 className="font-bold text-purple-900 mb-2">Talentfunn Pluss kreves</h3>
          <p className="text-sm text-purple-800 mb-4">
            Du trenger Talentfunn Pluss for å opprette og administrere tjenester.
          </p>
          <button
            onClick={() => {
              closeWithCleanup();
              onNavigateToSubscription?.();
            }}
            className="w-full px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-semibold"
          >
            Se abonnement
          </button>
        </div>
      ) : (
        <>
          {/* Cover-bilde øverst */}
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">Cover-bilde</label>
            <p className="text-sm text-gray-500 mb-2">Dette bildet vises på tjenestekortet.</p>
            {displayCover ? (
              <div className="relative">
                <img src={displayCover} alt="Cover preview" className="w-full aspect-[4/3] object-cover rounded-2xl border-2 border-purple-300" />
                <button
                  type="button"
                  onClick={removeCoverImage}
                  className="absolute top-3 right-3 p-2 bg-red-600 text-white rounded-full hover:bg-red-700 shadow-lg"
                >
                  <X className="w-5 h-5" />
                </button>
                <div className="absolute bottom-3 left-3 px-3 py-1.5 bg-purple-600 bg-opacity-90 text-white rounded-full text-sm font-medium">Cover-bilde</div>
                <label
                  htmlFor="cover-image-change"
                  className={`absolute bottom-3 right-3 px-4 py-2 bg-purple-600 bg-opacity-90 text-white rounded-full text-sm font-medium transition-all ${
                    compressing ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:bg-opacity-100'
                  }`}
                >
                  Endre
                  <input
                    id="cover-image-change"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleCoverImageChange}
                    disabled={compressing}
                  />
                </label>
              </div>
            ) : (
              <label
                htmlFor="cover-image-upload"
                className={`flex flex-col items-center justify-center w-full aspect-[4/3] border-2 border-dashed border-purple-400 rounded-2xl transition-all ${
                  compressing ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:border-purple-600 hover:bg-purple-50'
                }`}
              >
                {compressing ? (
                  <span className="text-sm text-gray-500">Behandler...</span>
                ) : (
                  <>
                    <Upload className="w-12 h-12 text-purple-500 mb-2" />
                    <p className="text-purple-700 font-medium">Klikk for å laste opp cover-bilde</p>
                    <p className="text-purple-400 text-sm">PNG, JPG eller JPEG</p>
                  </>
                )}
                <input
                  id="cover-image-upload"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleCoverImageChange}
                  disabled={compressing}
                />
              </label>
            )}
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">Tittel</label>
            <input
              value={title}
              onChange={(e) => { userHasEditedSinceApply.current = true; markDirty(); setTitle(e.target.value); }}
              maxLength={100}
              className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder="F.eks. Bryllupsfoto"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">Beskrivelse</label>
            <textarea
              value={description}
              onChange={(e) => { userHasEditedSinceApply.current = true; markDirty(); setDescription(e.target.value); }}
              maxLength={2000}
              rows={5}
              className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
              placeholder="Beskriv hva tjenesten inkluderer..."
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">Min pris (NOK)</label>
              <input
                value={priceMin}
                onChange={(e) => { userHasEditedSinceApply.current = true; markDirty(); setPriceMin(e.target.value); }}
                inputMode="numeric"
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="1500"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">Maks pris (NOK)</label>
              <input
                value={priceMax}
                onChange={(e) => { userHasEditedSinceApply.current = true; markDirty(); setPriceMax(e.target.value); }}
                inputMode="numeric"
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="2500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">Sted (valgfritt)</label>
            <input
              value={county}
              onChange={(e) => { userHasEditedSinceApply.current = true; markDirty(); setCounty(e.target.value); }}
              className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder="F.eks. Oslo"
            />
          </div>

          {/* Bilder av tjenesten – opptil 3 */}
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">Bilder av tjenesten</label>
            <p className="text-sm text-gray-500 mb-2">Opptil 3 bilder.</p>
            <div className="flex flex-wrap gap-3">
              {existingImageUrls.map((url, index) => (
                <div key={`ex-${index}`} className="relative">
                  <img src={url} alt="" className="w-28 aspect-[4/3] object-cover rounded-lg border border-gray-200" />
                  <button
                    type="button"
                    onClick={() => removeExistingImage(index)}
                    className="absolute -top-2 -right-2 p-1 bg-red-600 text-white rounded-full hover:bg-red-700"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
              {newImagePreviews.map((dataUrl, index) => (
                <div key={`new-${index}`} className="relative">
                  <img src={dataUrl} alt="" className="w-28 aspect-[4/3] object-cover rounded-lg border border-gray-200" />
                  <button
                    type="button"
                    onClick={() => removeNewImage(index)}
                    className="absolute -top-2 -right-2 p-1 bg-red-600 text-white rounded-full hover:bg-red-700"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
              {remainingSlots > 0 && (
                <label className="flex flex-col items-center justify-center w-28 aspect-[4/3] border-2 border-dashed border-purple-300 rounded-lg cursor-pointer hover:border-purple-500 hover:bg-purple-50 transition-colors">
                  {compressing ? (
                    <span className="text-xs text-gray-500">Behandler...</span>
                  ) : (
                    <>
                      <Upload className="w-6 h-6 text-purple-500 mb-0.5" />
                      <span className="text-xs text-purple-700">Legg til</span>
                    </>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={handleImageChange}
                    disabled={compressing}
                  />
                </label>
              )}
            </div>
          </div>

          <button
            onClick={handleSubmit}
            disabled={submitting || compressing}
            className="w-full px-6 py-3 rounded-lg bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold hover:shadow-lg disabled:opacity-60"
          >
            {submitting ? 'Lagrer...' : isEditMode ? 'Lagre endringer' : 'Opprett tjeneste'}
          </button>
        </>
      )}
    </FullscreenShell>
    </>
  );
}
